import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { changelog, exportCsv, funnels, opportunities } from "@/api/endpoints";
import { ApiError } from "@/api/client";
import type {
  CapacityUnit,
  ChangelogEntry,
  Funnel,
  FunnelKey,
  MetricDefinition,
  Opportunity,
  OppStatus,
  Stage,
  ValueType,
} from "@/types/api";
import { useCurrentUser, canEdit } from "@/hooks/useAuth";
import { useToast } from "@/store/toastStore";
import { capacityLabel, formatDateTime, formatDays } from "@/lib/format";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Select,
  Spinner,
  TextInput,
} from "@/components/ui";

const FUNNEL_KEYS: FunnelKey[] = ["customer", "hardware", "financing"];
const STATUS_COLORS: Record<OppStatus, "blue" | "green" | "red"> = {
  open: "blue",
  won: "green",
  lost: "red",
};
const CAPACITY_UNITS: CapacityUnit[] = ["gpu", "node", "usd"];
const VALUE_TYPES: ValueType[] = ["number", "enum", "date", "text"];

type StatusFilter = OppStatus | "all";

function errMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Something went wrong";
}

/* ----------------------------------------------------------------- Quick-add */

function QuickAdd({ funnelId, funnelKey }: { funnelId: string; funnelKey: FunnelKey }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [capacityTotal, setCapacityTotal] = useState("");
  const [capacityUnit, setCapacityUnit] = useState<CapacityUnit>("gpu");

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => opportunities.create(body),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["opportunities", funnelKey] });
      setTitle("");
      setCapacityTotal("");
      toast("success", "Opportunity created");
      // Open the new opportunity so details can be filled in immediately.
      navigate(`/opportunities/${created.id}`);
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    if (!t) {
      toast("error", "Title is required");
      return;
    }
    const body: Record<string, unknown> = { funnel_id: funnelId, title: t };
    const cap = capacityTotal.trim();
    if (cap) {
      const n = Number(cap);
      if (Number.isNaN(n)) {
        toast("error", "Capacity must be a number");
        return;
      }
      body.capacity_total = n;
      body.capacity_unit = capacityUnit;
    }
    create.mutate(body);
  };

  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
      <div className="min-w-[14rem] flex-1">
        <Field label="Title">
          <TextInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="New opportunity"
          />
        </Field>
      </div>
      <div className="w-32">
        <Field label="Capacity">
          <TextInput
            type="number"
            value={capacityTotal}
            onChange={(e) => setCapacityTotal(e.target.value)}
            placeholder="optional"
          />
        </Field>
      </div>
      <div className="w-28">
        <Field label="Unit">
          <Select
            value={capacityUnit}
            onChange={(e) => setCapacityUnit(e.target.value as CapacityUnit)}
            options={CAPACITY_UNITS.map((u) => ({ value: u, label: u }))}
          />
        </Field>
      </div>
      <Button type="submit" disabled={create.isPending}>
        {create.isPending ? <Spinner /> : "Add"}
      </Button>
    </form>
  );
}

/* ------------------------------------------------------------- OppCard (row) */

function OppCard({
  opp,
  stages,
  funnelKey,
  editable,
}: {
  opp: Opportunity;
  stages: Stage[];
  funnelKey: FunnelKey;
  editable: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const move = useMutation({
    mutationFn: (toStageId: string) => opportunities.move(opp.id, toStageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opportunities", funnelKey] });
      toast("success", "Stage updated");
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5 shadow-sm">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Link
          to={`/opportunities/${opp.id}`}
          className="truncate text-sm font-medium text-slate-900 hover:underline"
        >
          {opp.title}
        </Link>
        <Badge color={STATUS_COLORS[opp.status]}>{opp.status}</Badge>
      </div>
      <div className="flex items-center gap-3 text-xs text-slate-500">
        {opp.owner_user_id && <span className="text-slate-400">owner</span>}
        <span>{capacityLabel(opp.capacity_total, opp.capacity_unit)}</span>
        <span className="text-slate-300">·</span>
        <span>{formatDays(opp.days_in_stage)} in stage</span>
        {editable && (
          <Select
            value={opp.stage_id}
            disabled={move.isPending}
            onChange={(e) => {
              const to = e.target.value;
              if (to !== opp.stage_id) move.mutate(to);
            }}
            options={stages.map((s) => ({ value: s.id, label: s.name }))}
          />
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------- StageSection (vertical) */

function StageSection({
  stage,
  opps,
  stages,
  funnelKey,
  editable,
  position,
  total,
}: {
  stage: Stage;
  opps: Opportunity[];
  stages: Stage[];
  funnelKey: FunnelKey;
  editable: boolean;
  position: number;
  total: number;
}) {
  return (
    <div className="relative pl-8">
      {/* vertical funnel spine */}
      <div className="absolute left-3 top-0 flex h-full flex-col items-center">
        <div className="h-3 w-3 rounded-full border-2 border-slate-400 bg-white" />
        {position < total - 1 && <div className="w-px flex-1 bg-slate-300" />}
      </div>
      <div className="mb-2 flex items-center gap-2">
        <span className="text-sm font-semibold text-slate-900">{stage.name}</span>
        <Badge color="slate">{opps.length}</Badge>
        {stage.is_deploy_ready && <Badge color="green">deploy-ready</Badge>}
        {stage.is_lost && <Badge color="red">lost</Badge>}
      </div>
      <div className="space-y-2 pb-6">
        {opps.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 px-4 py-3 text-xs text-slate-400">
            No opportunities in this stage
          </div>
        ) : (
          opps.map((opp) => (
            <OppCard
              key={opp.id}
              opp={opp}
              stages={stages}
              funnelKey={funnelKey}
              editable={editable}
            />
          ))
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------ Funnel settings: StageRow */

function StageRow({
  stage,
  index,
  count,
  funnelId,
  siblingIds,
  editable,
}: {
  stage: Stage;
  index: number;
  count: number;
  funnelId: string;
  siblingIds: string[];
  editable: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["funnels"] });

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => funnels.updateStage(stage.id, body),
    onSuccess: () => {
      invalidate();
      toast("success", "Stage updated");
    },
    onError: (e) => toast("error", errMessage(e)),
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => funnels.reorderStages(funnelId, ids),
    onSuccess: () => {
      invalidate();
      toast("success", "Stages reordered");
    },
    onError: (e) => toast("error", errMessage(e)),
  });
  const archive = useMutation({
    mutationFn: () => funnels.archiveStage(stage.id),
    onSuccess: () => {
      invalidate();
      toast("success", "Stage archived");
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  const [name, setName] = useState(stage.name);
  const [duration, setDuration] = useState(
    stage.target_duration_days === null ? "" : String(stage.target_duration_days),
  );
  const busy = update.isPending || reorder.isPending || archive.isPending;

  const submitRename = () => {
    const t = name.trim();
    if (!t || t === stage.name) {
      setName(stage.name);
      return;
    }
    update.mutate({ name: t });
  };
  const submitDuration = () => {
    const raw = duration.trim();
    let value: number | null;
    if (raw === "") value = null;
    else {
      const n = Number(raw);
      if (Number.isNaN(n) || n < 0) {
        toast("error", "Target duration must be a non-negative number");
        setDuration(stage.target_duration_days === null ? "" : String(stage.target_duration_days));
        return;
      }
      value = n;
    }
    if (value === stage.target_duration_days) return;
    update.mutate({ target_duration_days: value });
  };
  const moveStage = (dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= count) return;
    const ids = [...siblingIds];
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorder.mutate(ids);
  };

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3">
      <div className="min-w-[12rem] flex-1">
        <Field label={`Stage ${index + 1}`}>
          <TextInput
            value={name}
            disabled={!editable || busy}
            onChange={(e) => setName(e.target.value)}
            onBlur={submitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
          />
        </Field>
      </div>
      <div className="w-28">
        <Field label="Target days">
          <TextInput
            type="number"
            min={0}
            value={duration}
            disabled={!editable || busy}
            placeholder="none"
            onChange={(e) => setDuration(e.target.value)}
            onBlur={submitDuration}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
          />
        </Field>
      </div>
      <div className="flex items-center gap-4 pb-1.5">
        <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300"
            checked={stage.is_deploy_ready}
            disabled={!editable || busy}
            onChange={(e) => update.mutate({ is_deploy_ready: e.target.checked })}
          />
          Deploy-ready
        </label>
        <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300"
            checked={stage.is_lost}
            disabled={!editable || busy}
            onChange={(e) => update.mutate({ is_lost: e.target.checked })}
          />
          Lost
        </label>
      </div>
      {editable && (
        <div className="flex items-center gap-1.5 pb-0.5">
          <Button variant="secondary" size="sm" disabled={busy || index === 0} onClick={() => moveStage(-1)}>
            ↑
          </Button>
          <Button variant="secondary" size="sm" disabled={busy || index === count - 1} onClick={() => moveStage(1)}>
            ↓
          </Button>
          <Button variant="danger" size="sm" disabled={busy} onClick={() => archive.mutate()}>
            Archive
          </Button>
        </div>
      )}
    </div>
  );
}

function AddStageForm({ funnelId, editable }: { funnelId: string; editable: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [duration, setDuration] = useState("");

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => funnels.addStage(funnelId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["funnels"] });
      setName("");
      setDuration("");
      toast("success", "Stage added");
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  if (!editable) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = name.trim();
    if (!t) {
      toast("error", "Stage name is required");
      return;
    }
    const body: Record<string, unknown> = { name: t, is_deploy_ready: false, is_lost: false };
    const raw = duration.trim();
    body.target_duration_days = raw ? Number(raw) : null;
    if (raw && (Number.isNaN(Number(raw)) || Number(raw) < 0)) {
      toast("error", "Target duration must be a non-negative number");
      return;
    }
    create.mutate(body);
  };

  return (
    <form
      onSubmit={submit}
      className="mt-2 flex flex-wrap items-end gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3"
    >
      <div className="min-w-[12rem] flex-1">
        <Field label="New stage name">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Qualified" />
        </Field>
      </div>
      <div className="w-28">
        <Field label="Target days">
          <TextInput
            type="number"
            min={0}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="optional"
          />
        </Field>
      </div>
      <Button type="submit" disabled={create.isPending}>
        {create.isPending ? <Spinner /> : "Add stage"}
      </Button>
    </form>
  );
}

/* ------------------------------------------- Funnel settings: metrics + log */

function FunnelMetrics({ funnel, editable }: { funnel: Funnel; editable: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const defsQuery = useQuery({
    queryKey: ["metric-definitions"],
    queryFn: () => funnels.metricDefinitions(),
  });

  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [valueType, setValueType] = useState<ValueType>("number");
  const [enumOptions, setEnumOptions] = useState("");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["metric-definitions"] });
  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => funnels.createMetricDefinition(body),
    onSuccess: () => {
      invalidate();
      setKey("");
      setLabel("");
      setValueType("number");
      setEnumOptions("");
      toast("success", "Metric created");
    },
    onError: (e) => toast("error", errMessage(e)),
  });
  const archive = useMutation({
    mutationFn: (id: string) => funnels.archiveMetricDefinition(id),
    onSuccess: () => {
      invalidate();
      toast("success", "Metric archived");
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  const defs = useMemo(
    () =>
      (defsQuery.data ?? [])
        .filter((d) => !d.archived_at && d.funnel_id === funnel.id)
        .sort((a, b) => a.position - b.position),
    [defsQuery.data, funnel.id],
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const k = key.trim();
    const l = label.trim();
    if (!k || !l) {
      toast("error", "Key and label are required");
      return;
    }
    const body: Record<string, unknown> = {
      key: k,
      label: l,
      value_type: valueType,
      funnel_id: funnel.id,
    };
    if (valueType === "enum") {
      const opts = enumOptions.split(",").map((s) => s.trim()).filter(Boolean);
      if (opts.length === 0) {
        toast("error", "Enum metrics need at least one option");
        return;
      }
      body.enum_options = opts;
    } else body.enum_options = null;
    create.mutate(body);
  };

  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Metrics
      </div>
      {defs.length === 0 ? (
        <p className="text-xs text-slate-400">No metrics defined for this funnel.</p>
      ) : (
        <ul className="space-y-1">
          {defs.map((d: MetricDefinition) => (
            <li key={d.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="flex items-center gap-2">
                <span className="font-mono text-xs text-slate-600">{d.key}</span>
                <span className="text-slate-900">{d.label}</span>
                <Badge color="blue">{d.value_type}</Badge>
              </span>
              {editable && (
                <Button variant="danger" size="sm" disabled={archive.isPending} onClick={() => archive.mutate(d.id)}>
                  Archive
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <form onSubmit={submit} className="mt-2 flex flex-wrap items-end gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
          <div className="w-36">
            <Field label="Key">
              <TextInput value={key} onChange={(e) => setKey(e.target.value)} placeholder="e.g. region" />
            </Field>
          </div>
          <div className="w-40">
            <Field label="Label">
              <TextInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Display label" />
            </Field>
          </div>
          <div className="w-28">
            <Field label="Type">
              <Select value={valueType} onChange={(e) => setValueType(e.target.value as ValueType)} options={VALUE_TYPES.map((v) => ({ value: v, label: v }))} />
            </Field>
          </div>
          {valueType === "enum" && (
            <div className="w-52">
              <Field label="Enum options" hint="Comma-separated">
                <TextInput value={enumOptions} onChange={(e) => setEnumOptions(e.target.value)} placeholder="us-east, us-west" />
              </Field>
            </div>
          )}
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? <Spinner /> : "Add metric"}
          </Button>
        </form>
      )}
    </div>
  );
}

function FunnelChangelog({ funnel }: { funnel: Funnel }) {
  const logQuery = useQuery({ queryKey: ["changelog"], queryFn: () => changelog.list() });
  const entries = useMemo(() => {
    const all = logQuery.data ?? [];
    return all.filter((e: ChangelogEntry) => {
      const fid = (e.detail as Record<string, unknown> | null)?.funnel_id;
      return fid === funnel.id || e.summary.toLowerCase().includes(funnel.name.toLowerCase());
    });
  }, [logQuery.data, funnel.id, funnel.name]);

  if (entries.length === 0) return null;
  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Recent configuration changes
      </div>
      <ul className="space-y-2">
        {entries.slice(0, 10).map((entry) => (
          <li key={entry.id} className="flex items-start justify-between gap-3 text-sm">
            <span className="text-slate-800">{entry.summary}</span>
            <span className="shrink-0 text-xs text-slate-400">{formatDateTime(entry.created_at)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FunnelSettings({ funnel, editable }: { funnel: Funnel; editable: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(funnel.name);

  const rename = useMutation({
    mutationFn: (newName: string) => funnels.rename(funnel.id, newName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["funnels"] });
      toast("success", "Funnel renamed");
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  const stages = useMemo(
    () => funnel.stages.filter((s) => !s.archived_at).sort((a, b) => a.position - b.position),
    [funnel.stages],
  );
  const stageIds = useMemo(() => stages.map((s) => s.id), [stages]);

  const submitRename = () => {
    const t = name.trim();
    if (!t || t === funnel.name) {
      setName(funnel.name);
      return;
    }
    rename.mutate(t);
  };

  return (
    <div className="space-y-5">
      <div className="max-w-md">
        <Field label="Funnel name">
          <TextInput
            value={name}
            disabled={!editable || rename.isPending}
            onChange={(e) => setName(e.target.value)}
            onBlur={submitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
          />
        </Field>
      </div>
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Stages</div>
        <div className="space-y-2">
          {stages.map((stage, i) => (
            <StageRow
              key={stage.id}
              stage={stage}
              index={i}
              count={stages.length}
              funnelId={funnel.id}
              siblingIds={stageIds}
              editable={editable}
            />
          ))}
        </div>
        <AddStageForm funnelId={funnel.id} editable={editable} />
      </div>
      <FunnelMetrics funnel={funnel} editable={editable} />
      <FunnelChangelog funnel={funnel} />
    </div>
  );
}

/* ---------------------------------------------------------------------- Page */

export default function FunnelBoard() {
  const { key } = useParams<{ key: string }>();
  const funnelKey = (FUNNEL_KEYS.includes(key as FunnelKey) ? key : undefined) as FunnelKey | undefined;

  const user = useCurrentUser();
  const editable = canEdit(user?.role);

  const [ownerFilter, setOwnerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [showSettings, setShowSettings] = useState(false);

  const funnelsQuery = useQuery({ queryKey: ["funnels"], queryFn: () => funnels.list() });
  const oppsQuery = useQuery({
    queryKey: ["opportunities", funnelKey],
    queryFn: () => opportunities.list({ funnel: funnelKey }),
    enabled: !!funnelKey,
  });

  const funnel: Funnel | undefined = useMemo(
    () => funnelsQuery.data?.find((f) => f.key === funnelKey),
    [funnelsQuery.data, funnelKey],
  );
  const stages = useMemo(
    () => (funnel?.stages ?? []).filter((s) => !s.archived_at).sort((a, b) => a.position - b.position),
    [funnel],
  );
  const filtered = useMemo(() => {
    const owner = ownerFilter.trim().toLowerCase();
    return (oppsQuery.data ?? []).filter((o) => {
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (owner && !(o.owner_user_id ?? "").toLowerCase().includes(owner)) return false;
      return true;
    });
  }, [oppsQuery.data, ownerFilter, statusFilter]);
  const byStage = useMemo(() => {
    const map = new Map<string, Opportunity[]>();
    for (const s of stages) map.set(s.id, []);
    for (const o of filtered) {
      const list = map.get(o.stage_id);
      if (list) list.push(o);
    }
    return map;
  }, [stages, filtered]);

  if (!funnelKey) return <EmptyState message={`Unknown funnel: ${key ?? ""}`} />;
  if (funnelsQuery.isLoading)
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Spinner /> Loading funnel…
      </div>
    );
  if (funnelsQuery.isError) return <EmptyState message={errMessage(funnelsQuery.error)} />;
  if (!funnel) return <EmptyState message={`Funnel "${funnelKey}" not found`} />;

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-slate-900">{funnel.name}</h1>
        <div className="flex items-center gap-2">
          {editable && (
            <Button variant="secondary" size="sm" onClick={() => setShowSettings((v) => !v)}>
              {showSettings ? "Hide settings" : "Funnel settings"}
            </Button>
          )}
          <a
            href={exportCsv.opportunities(funnelKey)}
            className="inline-flex items-center justify-center gap-1.5 rounded border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Export CSV
          </a>
        </div>
      </div>

      {showSettings && editable && (
        <Card title="Funnel settings" className="mb-6">
          <FunnelSettings funnel={funnel} editable={editable} />
        </Card>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-44">
          <Field label="Owner">
            <TextInput value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} placeholder="Filter by owner" />
          </Field>
        </div>
        <div className="w-36">
          <Field label="Status">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              options={[
                { value: "all", label: "All" },
                { value: "open", label: "Open" },
                { value: "won", label: "Won" },
                { value: "lost", label: "Lost" },
              ]}
            />
          </Field>
        </div>
      </div>

      {editable && (
        <Card title="Quick add" className="mb-6">
          <QuickAdd funnelId={funnel.id} funnelKey={funnelKey} />
        </Card>
      )}

      {stages.length === 0 ? (
        <EmptyState message="This funnel has no stages" />
      ) : oppsQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Spinner /> Loading opportunities…
        </div>
      ) : oppsQuery.isError ? (
        <EmptyState message={errMessage(oppsQuery.error)} />
      ) : (
        <div>
          {stages.map((stage, i) => (
            <StageSection
              key={stage.id}
              stage={stage}
              opps={byStage.get(stage.id) ?? []}
              stages={stages}
              funnelKey={funnelKey}
              editable={editable}
              position={i}
              total={stages.length}
            />
          ))}
        </div>
      )}
    </div>
  );
}
